"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useWaveWebSocket } from "./useWaveWebSocket";
import type { WsDropUpdateMessage, WsTypingMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface TypingEntry {
  profile: ApiProfileMin;
  lastTypingAt: number; // our local receive time (ms)
}

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

const TYPING_WINDOW_MS = 5_000; // still typing if ≤ 5 s old
const CLEANUP_INTERVAL_MS = 1_000; // prune/check once per second

/* ------------------------------------------------------------------ */
/*  Helper to convert active typers → human string                    */
/* ------------------------------------------------------------------ */

function buildTypingString(entries: TypingEntry[]): string {
  if (entries.length === 0) return "";

  // Highest‑level first (undefined → 0)
  const sorted = entries.sort((a, b) => (b.profile.level ?? 0) - (a.profile.level ?? 0));
  const names = sorted.map(e => e.profile.handle);
  if (names.length === 1) {
    return `${names[0]} is typing`;
  }
  if (names.length === 2) {
    return `${names[0]}, ${names[1]} are typing`;
  }
  return `${names[0]}, ${names[1]} and ${names.length - 2} more people are typing`;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

/**
 * React hook that returns a live "is‑typing" label for a wave.
 *
 * @param waveId    Wave/channel ID being viewed.
 * @param myHandle  Handle of current user (events from this handle are ignored).
 * @param disabled  If true, skip websocket subscription (e.g., for muted waves).
 */
export function useWaveIsTyping(waveId, myHandle, t0) {
  const $ = _c(12);
  const disabled = t0 === undefined ? false : t0;
  const {
    socket
  } = useWaveWebSocket(disabled ? "" : waveId);
  const [typingMessage, setTypingMessage] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const typersRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      typersRef.current.clear();
      setTypingMessage("");
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disabled || $[3] !== waveId) {
    t3 = [waveId, disabled];
    $[2] = disabled;
    $[3] = waveId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== myHandle || $[6] !== socket || $[7] !== waveId) {
    t4 = () => {
      if (!socket) {
        return;
      }
      const onMessage = event => {
        let msg;
        ;
        try {
          msg = JSON.parse(event.data);
        } catch (t6) {
          const err = t6;
          console.error("Bad WebSocket JSON", err);
          return;
        }
        if (msg.type === WsMessageType.DROP_UPDATE) {
          typersRef.current.delete(msg.data?.author.handle ?? "");
        }
        if (msg.type !== WsMessageType.USER_IS_TYPING) {
          return;
        }
        const data = msg.data;
        if (!data || data.wave_id !== waveId) {
          return;
        }
        if (data.profile?.handle === myHandle) {
          return;
        }
        if (!data.profile?.handle) {
          return;
        }
        typersRef.current.set(data.profile.handle, {
          profile: data.profile,
          lastTypingAt: Date.now()
        });
      };
      const currentSocket = socket;
      currentSocket.addEventListener("message", onMessage);
      return () => {
        if (currentSocket) {
          currentSocket.removeEventListener("message", onMessage);
        }
      };
    };
    t5 = [socket, waveId, myHandle];
    $[5] = myHandle;
    $[6] = socket;
    $[7] = waveId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const intervalId = setInterval(() => {
        const now = Date.now();
        typersRef.current.forEach((entry, handle) => {
          if (now - entry.lastTypingAt > TYPING_WINDOW_MS) {
            typersRef.current.delete(handle);
          }
        });
        const newMessage = buildTypingString(Array.from(typersRef.current.values()));
        setTypingMessage(prev => prev === newMessage ? prev : newMessage);
      }, CLEANUP_INTERVAL_MS);
      return () => clearInterval(intervalId);
    };
    t7 = [];
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  return typingMessage;
}